import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, CaretRight } from "@phosphor-icons/react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Newsletter } from "../components/Newsletter";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="bg-[#F1EEE8] min-h-screen text-[#232326]" data-testid="blog-post-not-found">
        <Nav />
        <div className="max-w-3xl mx-auto px-6 pt-40 pb-24 text-center">
          <div className="text-[0.7rem] tracking-[0.28em] uppercase text-[#8A6F45] mb-4">404</div>
          <h1 className="font-display text-4xl md:text-6xl leading-tight mb-6">
            Articolo non trovato.
          </h1>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[0.75rem] tracking-[0.24em] uppercase text-[#13535A] hover:text-[#232326] transition-colors"
          >
            <ArrowLeft size={14} weight="bold" />
            Torna al blog
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  if (!post) {
    return (
      <main className="bg-[#F1EEE8] min-h-screen">
        <Nav />
      </main>
    );
  }

  const pick = (field) => post?.[`${field}_${lang}`] || post?.[`${field}_it`] || post?.[`${field}_en`] || "";
  const title = pick("title");
  const category = pick("category");
  const excerpt = pick("excerpt");
  const body = pick("body");

  return (
    <main
      className="bg-[#F1EEE8] text-[#232326] min-h-screen"
      data-testid="blog-post-page"
    >
      <Nav />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="mb-6 flex items-center gap-3 text-[0.7rem] tracking-[0.24em] uppercase">
            <Link to="/blog" className="text-[#232326]/60 hover:text-[#13535A] transition-colors flex items-center gap-2">
              <ArrowLeft size={12} weight="bold" />
              Blog
            </Link>
            <CaretRight size={12} className="text-[#232326]/30" />
            <span className="text-[#8A6F45] font-semibold">{category}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.7, 0, 0.2, 1] }}
            className="font-display font-medium leading-[1.06] tracking-tight text-[clamp(2.2rem,5vw,4.4rem)] mb-6"
          >
            {title}
          </motion.h1>
          <div className="text-[#8A6F45] text-[0.7rem] tracking-[0.24em] uppercase mb-6 font-medium">
            {post.date}
          </div>
          <p className="text-[#232326]/75 text-lg md:text-xl leading-relaxed italic-serif">
            {excerpt}
          </p>
        </div>
      </section>

      {/* Cover image */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 mb-14 md:mb-20">
        <div className="relative aspect-[16/9] overflow-hidden clip-card">
          <img
            src={`${post.image}?auto=format&fit=crop&w=2400&q=90`}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <div className="prose-zitro text-[#232326]/90 leading-[1.85] text-[1.05rem] md:text-[1.12rem]" data-testid="blog-body">
          {body?.split("\n\n").map((para, i) => (
            <p key={i} className="mb-6">
              {para}
            </p>
          ))}
        </div>
      </article>

      <Newsletter />
      <Footer />
    </main>
  );
}
